import React, { createContext, useContext, useState } from 'react'
import { useUnsafeMemo } from '@factorialco/deprecated-design-system'

import { ApolloError } from '@apollo/client'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { LoadingWithSpinner } from 'modules/finance/components/FinancialWorkspace/components/LoadingWithSpinner'
import { PlotData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import {
  EvolutionChartProps,
  useEvolutionChartData,
} from 'modules/finance/components/FinancialWorkspace/hooks/charts/useEvolutionChartData'

interface Props {
  children: React.ReactNode
}

interface IValue {
  loading: boolean
  plotData?: {
    label: string
    data: PlotData[]
    id: string | number
  }[]
  error: ApolloError | undefined
  chartProps: EvolutionChartProps
}

const TotalProviderContext = createContext<IValue | undefined>(undefined)

export const TotalProvider: React.FC<Props> = ({ children }) => {
  const { savedFilters } = useFilterProvider()
  const {
    formattedState: {
      period,
      conceptIds,
      locationIds,
      employeeIds,
      itemsPerPage,
      costCenterIds,
      legalEntityIds,
      referenceContractTreeLevelUuids,
      formattedEndDate,
      insightTableType,
      formattedStartDate,
      includeFuturePeriods,
      formattedCompareToEndDate,
      formattedCompareToStartDate,
    },
  } = useFinancialWorkspace()

  const variables = useUnsafeMemo(() => {
    return {
      period,
      locationIds,
      employeeIds,
      itemsPerPage,
      costCenterIds,
      legalEntityIds,
      referenceContractTreeLevelUuids,
      formattedEndDate,
      insightTableType,
      formattedStartDate,
      includeFuturePeriods,
      formattedCompareToEndDate,
      formattedCompareToStartDate,
    }
  }, [
    period,
    locationIds,
    employeeIds,
    itemsPerPage,
    costCenterIds,
    legalEntityIds,
    referenceContractTreeLevelUuids,
    formattedEndDate,
    insightTableType,
    formattedStartDate,
    includeFuturePeriods,
    formattedCompareToEndDate,
    formattedCompareToStartDate,
  ])

  // By default
  // If concepts are selected, we load data for selected concepts
  // Otherwise, we load data up to 4 concepts.
  const buildChartProps = useUnsafeCallback(() => {
    const conceptIdsFilter = savedFilters.conceptIds || conceptIds
    const skip = conceptIdsFilter && conceptIdsFilter.length > 3

    const props: EvolutionChartProps = {
      skip,
      variables: { ...variables },
    }

    if (conceptIdsFilter?.length) {
      props.variables.conceptIds = conceptIdsFilter
    } else {
      props.limit = 4
    }

    return props
  }, [variables, savedFilters.conceptIds])

  const [chartProps, setChartProps] = useState<EvolutionChartProps>(buildChartProps())

  useUnsafeEffect(() => {
    setChartProps(buildChartProps())
  }, [buildChartProps])

  const { error, loading, items } = useEvolutionChartData(chartProps)

  // If no concepts are selected and data loaded returns more than 3 concepts,
  // We load data for only company_cost then. Since we don't want to show more that 3 concepts at a time
  useUnsafeEffect(() => {
    if (!chartProps?.skip && !error && !loading) {
      if (chartProps.limit === 4 && items.length > 3) {
        const props = buildChartProps()
        props.limit = undefined
        props.costType = 'company_cost'
        props.variables.conceptIds = undefined
        setChartProps(props)
      }
    }
  }, [error, loading, items, chartProps, buildChartProps])

  const plotData = items?.map(({ id, data, label }) => {
    return {
      id,
      label: label || '',
      data: data.map((item) => {
        const totalGrossSalaryInCents = Number(item.totalAmountInCents)

        return {
          x: item.label,
          title: item.label,
          y: totalGrossSalaryInCents,
          totalAmountWithPrecision: item.totalAmountWithPrecision,
        }
      }),
    }
  })

  if (loading) {
    return <LoadingWithSpinner />
  }

  return (
    <TotalProviderContext.Provider value={{ error, loading, plotData, chartProps }}>
      {children}
    </TotalProviderContext.Provider>
  )
}

export const useTotal = () => {
  const context = useContext(TotalProviderContext)
  if (context === undefined) {
    throw new Error('useTotal must be used within a TotalProvider')
  }
  return context
}
